import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Loader2, Save, User, MapPin, Briefcase, FileText, Star } from "lucide-react";
import type { Applicant, SpecialCertification } from "@/types/applicant";
import { LicenseAndCertificationsForm } from "@/components/LicenseAndCertificationsForm";
import ApplicantContactAttempts from "./ApplicantContactAttempts";

interface ApplicantDataFormProps {
  applicant: Applicant;
  onUpdate: () => void;
}

const ApplicantDataForm = ({ applicant, onUpdate }: ApplicantDataFormProps) => {
  const [formData, setFormData] = useState({
    first_name: applicant.first_name,
    last_name: applicant.last_name,
    email: applicant.email,
    phone: applicant.phone,
    address_street: applicant.address_street || "",
    address_zip: applicant.address_zip || "",
    address_city: applicant.address_city || "",
    address_country: applicant.address_country || "Deutschland",
    birth_date: applicant.birth_date || "",
    desired_position: applicant.desired_position || "",
    salary_expectation: applicant.salary_expectation || "",
    earliest_start_date: applicant.earliest_start_date || "",
    drivers_license: applicant.drivers_license || "",
    drivers_license_classes: Array.isArray(applicant.drivers_license_classes) ? applicant.drivers_license_classes : [],
    special_certifications: Array.isArray(applicant.special_certifications) ? applicant.special_certifications : [],
    security_clearance_valid_until: applicant.security_clearance_valid_until || "",
    qualifications: applicant.qualifications || "",
    notes: applicant.notes || "",
    interview_notes: applicant.interview_notes || "",
    references_info: applicant.references_info || "",
    rating: applicant.rating || 0,
  });
  const [saving, setSaving] = useState(false);
  const [jobPositions, setJobPositions] = useState<{ id: string; title: string }[]>([]);

  useEffect(() => {
    supabase
      .from("job_positions")
      .select("id, title")
      .eq("is_active", true)
      .order("sort_order", { ascending: true })
      .then(({ data }) => setJobPositions(data || []));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const { error } = await supabase
        .from("applicants")
        .update({
          first_name: formData.first_name,
          last_name: formData.last_name,
          email: formData.email,
          phone: formData.phone,
          address_street: formData.address_street || null,
          address_zip: formData.address_zip || null,
          address_city: formData.address_city || null,
          address_country: formData.address_country || null,
          birth_date: formData.birth_date || null,
          desired_position: formData.desired_position || null,
          salary_expectation: formData.salary_expectation ? Number(formData.salary_expectation) : null,
          earliest_start_date: formData.earliest_start_date || null,
          drivers_license: formData.drivers_license || null,
          drivers_license_classes: formData.drivers_license_classes,
          special_certifications: formData.special_certifications,
          security_clearance_valid_until: formData.security_clearance_valid_until || null,
          qualifications: formData.qualifications || null,
          notes: formData.notes || null,
          interview_notes: formData.interview_notes || null,
          references_info: formData.references_info || null,
          rating: formData.rating || null,
        })
        .eq("id", applicant.id);

      if (error) throw error;
      toast.success("Daten gespeichert");
      onUpdate();
    } catch (error) {
      console.error("Error saving applicant:", error);
      toast.error("Fehler beim Speichern");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            Persönliche Daten
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Vorname</Label>
              <Input
                value={formData.first_name}
                onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Nachname</Label>
              <Input
                value={formData.last_name}
                onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
              />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>E-Mail</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Telefon</Label>
              <Input
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Geburtsdatum</Label>
              <Input
                type="date"
                value={formData.birth_date}
                onChange={(e) => setFormData({ ...formData, birth_date: e.target.value })}
              />
            </div>
            {formData.birth_date && (
              <div className="space-y-2">
                <Label>Alter</Label>
                <div className="h-10 px-3 py-2 rounded-md border bg-muted flex items-center text-sm">
                  {(() => {
                    const birthDate = new Date(formData.birth_date);
                    const today = new Date();
                    let age = today.getFullYear() - birthDate.getFullYear();
                    const monthDiff = today.getMonth() - birthDate.getMonth();
                    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
                      age--;
                    }
                    return `${age} Jahre`;
                  })()}
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MapPin className="h-5 w-5" />
            Adresse
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Straße</Label>
            <Input
              value={formData.address_street}
              onChange={(e) => setFormData({ ...formData, address_street: e.target.value })}
            />
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>PLZ</Label>
              <Input
                value={formData.address_zip}
                onChange={(e) => setFormData({ ...formData, address_zip: e.target.value })}
              />
            </div>
            <div className="space-y-2 col-span-1 sm:col-span-2">
              <Label>Stadt</Label>
              <Input
                value={formData.address_city}
                onChange={(e) => setFormData({ ...formData, address_city: e.target.value })}
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Land</Label>
            <Input
              value={formData.address_country}
              onChange={(e) => setFormData({ ...formData, address_country: e.target.value })}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Briefcase className="h-5 w-5" />
            Berufliche Daten
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Gewünschte Position</Label>
            <Input
              list="job-positions-list-edit"
              value={formData.desired_position}
              onChange={(e) => setFormData({ ...formData, desired_position: e.target.value })}
              placeholder="Position auswählen oder eingeben"
            />
            <datalist id="job-positions-list-edit">
              {jobPositions.map((p) => (
                <option key={p.id} value={p.title} />
              ))}
            </datalist>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Gehaltsvorstellung (€)</Label>
              <Input
                type="number"
                value={formData.salary_expectation}
                onChange={(e) => setFormData({ ...formData, salary_expectation: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Frühester Eintrittstermin</Label>
              <Input
                type="date"
                value={formData.earliest_start_date}
                onChange={(e) => setFormData({ ...formData, earliest_start_date: e.target.value })}
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Qualifikationen</Label>
            <Textarea
              value={formData.qualifications}
              onChange={(e) => setFormData({ ...formData, qualifications: e.target.value })}
              rows={3}
              placeholder="Ausbildung, Zertifikate, Berufserfahrung..."
            />
          </div>
        </CardContent>
      </Card>

      {/* License and Certifications */}
      <LicenseAndCertificationsForm
        applicantId={applicant.id}
        driversLicenseClasses={formData.drivers_license_classes}
        specialCertifications={formData.special_certifications}
        securityClearanceValidUntil={formData.security_clearance_valid_until}
        onDriversLicenseChange={(classes) => setFormData({ ...formData, drivers_license_classes: classes })}
        onCertificationsChange={(certs) => setFormData({ ...formData, special_certifications: certs })}
        onSecurityClearanceChange={(date) => setFormData({ ...formData, security_clearance_valid_until: date })}
      />

      {/* Kontaktaufnahme Section */}
      <ApplicantContactAttempts applicantId={applicant.id} />

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Notizen & Bewertung
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Star className="h-4 w-4" />
              Bewertung
            </Label>
            <div className="flex gap-1">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => setFormData({ ...formData, rating: star })}
                  className="focus:outline-none"
                >
                  <Star
                    className={`h-6 w-6 ${
                      star <= formData.rating
                        ? "fill-amber-400 text-amber-400"
                        : "text-gray-300 hover:text-amber-200"
                    }`}
                  />
                </button>
              ))}
              {formData.rating > 0 && (
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, rating: 0 })}
                  className="ml-2 text-xs text-muted-foreground hover:underline"
                >
                  Zurücksetzen
                </button>
              )}
            </div>
          </div>
          <div className="space-y-2">
            <Label>Allgemeine Notizen</Label>
            <Textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={3}
            />
          </div>
          <div className="space-y-2">
            <Label>Interview-Notizen</Label>
            <Textarea
              value={formData.interview_notes}
              onChange={(e) => setFormData({ ...formData, interview_notes: e.target.value })}
              rows={3}
            />
          </div>
          <div className="space-y-2">
            <Label>Referenzen</Label>
            <Textarea
              value={formData.references_info}
              onChange={(e) => setFormData({ ...formData, references_info: e.target.value })}
              rows={3}
            />
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={saving}>
          {saving ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Speichern...
            </>
          ) : (
            <>
              <Save className="mr-2 h-4 w-4" />
              Speichern
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default ApplicantDataForm;
